import { Slider as MuiSlider } from '@mui/material';
import clsx from 'clsx';

import { SxProps } from '../../../types';
import { tokens } from '../../../theme/generated/tokens';
import { focusProps } from '../../../theme/utils';
import { mergeSx } from '../../../utils';

import { sliderClassNames } from './classNames';

export type SliderValue = number | [number, number];

export type SliderProps = {
  /**
   * Controlled value. Pass a 2-tuple (`[number, number]`) to render a
   * two-thumb range. The component is controlled-only — there is no
   * `defaultValue`.
   */
  value?: SliderValue;
  /**
   * Change callback. MUI synthesizes `event.target.value`, so RFF's
   * `input.onChange` can be passed directly.
   */
  onChange?: (event: Event, value: SliderValue, activeThumb: number) => void;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  /** Minimum value (default: 0) */
  min?: number;
  /** Maximum value (default: 100) */
  max?: number;
  disabled?: boolean;
  /** `data-testid` attribute value (root element) */
  testId?: string;
  /** Additional CSS class for the root element */
  className?: string;
  /** MUI sx prop — merged after internal styles so consumers keep winning */
  sx?: SxProps;
};

// Solid disc thumb; rail is 2px — same as the track — in bg.structural
// .surface-strong (the Switch inactive-track token).
const internalStyles = {
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    boxShadow: 'none',
    backgroundColor: tokens.theme.bg.selection.primary,
    // MUI paints its own shadow layer on the thumb's ::before — suppress it.
    '&:before': {
      display: 'none',
    },
    '&:hover': {
      backgroundColor: tokens.theme.bg.selection.primaryHover,
    },
    '&:not(.Mui-focusVisible):hover': {
      boxShadow: 'unset',
    },
    '&.Mui-focusVisible': {
      boxShadow: 'unset',
      '&:not(:active):after': {
        width: 15,
        height: 15,
        ...focusProps(),
      },
      border: 'none',
    },
    '&:active': {
      border: 'unset',
      backgroundColor: tokens.theme.bg.selection.primaryActive,
    },
    '&.Mui-disabled': {
      backgroundColor: tokens.theme.disabled.bgStrong,
    },
  },
  '& .MuiSlider-rail': {
    marginLeft: -1,
    height: 2,
    opacity: 1,
    backgroundColor: tokens.theme.bg.structural.surfaceStrong,
  },
  '& .MuiSlider-track': {
    height: 2,
    boxSizing: 'border-box',
    backgroundColor: tokens.theme.bg.selection.primary,
    '.Mui-disabled &': {
      backgroundColor: tokens.theme.disabled.bg,
    },
  },
} satisfies SxProps;

export const Slider = ({
  value,
  onChange,
  onFocus,
  onBlur,
  min,
  max,
  disabled,
  testId,
  className,
  sx,
}: SliderProps) => {
  const isRange = Array.isArray(value);

  return (
    <MuiSlider
      size="small"
      value={value}
      min={min}
      max={max}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(event, newValue, activeThumb) =>
        onChange?.(event, newValue as SliderValue, activeThumb)
      }
      data-testid={testId}
      className={clsx(sliderClassNames.root, className, {
        [sliderClassNames.range]: isRange,
        [sliderClassNames.disabled]: disabled,
      })}
      sx={mergeSx(internalStyles, sx)}
    />
  );
};

Slider.classNames = sliderClassNames;
